import * as React from 'react';

import * as Sentry from '@sentry/browser';
import { UseQueryResult } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { getPathForItemById } from '../../../api/quiz';
import { QuizItemCache } from '../../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { QuizOrFolder } from '../../../api/quiz/getQuizzes/types';

export type FolderNavItem = {
  name: string;
  url: string;
};

function getBreadcrumbs({
  quizFolderPath,
  quizzes,
  rootFolderNavItem,
}: {
  quizFolderPath: string;
  quizzes: Record<string, QuizOrFolder>;
  rootFolderNavItem: FolderNavItem;
}): FolderNavItem[] {
  if (!quizzes) {
    return [];
  }

  if (!quizFolderPath?.length) {
    return [];
  }

  const quiz = quizzes[quizFolderPath];
  if (!quiz) {
    return [];
  }

  const toBreadCrumb = ({
    name,
    uniqueId,
  }: {
    name: string;
    uniqueId: string;
  }): FolderNavItem => ({ name, url: `#quizzes/${uniqueId}` });

  const currentBreadCrumb = toBreadCrumb(quiz);

  try {
    const breadcrumbs = getPathForItemById({
      uniqueId: quizFolderPath,
      allItems: quizzes,
    })
      .map((uniqueId) => quizzes[uniqueId])
      .filter(Boolean)
      .map<FolderNavItem>(toBreadCrumb);

    return [rootFolderNavItem, ...breadcrumbs, currentBreadCrumb];
  } catch (error: unknown) {
    Sentry.captureException(error);
    return [rootFolderNavItem, currentBreadCrumb];
  }
}

export function useBreadcrumbs(
  quizFolderPath: string,
  quizzes: UseQueryResult<QuizItemCache>
) {
  const [breadcrumbs, setBreadcrumbs] = React.useState<FolderNavItem[]>([]);

  React.useEffect(() => {
    const crumbs = getBreadcrumbs({
      quizFolderPath: quizFolderPath,
      quizzes: quizzes.data?.itemById,
      rootFolderNavItem: {
        name: translate('Personal'),
        url: '#quizzes/',
      },
    });
    setBreadcrumbs(crumbs);
  }, [quizzes.data?.itemById, quizFolderPath, breadcrumbs.length]);

  return breadcrumbs;
}
